import React, { useEffect } from 'react';
import { PaystackButton } from 'react-paystack';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPayment, resetPayment } from '../../redux/actions/payment';
import { bookAppointmentAction } from "../../redux/actions/appointmentActions";
import { toast } from 'react-toastify';
import { baseUrl, PAYSTACK_PUBLIC_KEY } from '../../redux/actions/baseUrl';

export default function Paystack({ 
    amount, 
    email, 
    phone, 
    notes, 
    url, 
    compData, 
    paymentType = 'general', // 'appointment' or 'general'
    onPaymentSuccess,
    onPaymentError,
    buttonText = 'Pay Now',
    buttonClassName = 'bg-primary100 w-full text-white text-xl text-bold mt-5 px-16 py-3 rounded-xl',
    verificationEndpoint = null 
}) {
    const dispatch = useDispatch();
    const { loading, success, data, error } = useSelector((state) => state.createPayment);
    const appt = useSelector((state) => state.bookAppontment);

    let user = JSON.parse(localStorage.getItem('userInfo'));
    const navigate = useNavigate();

    const [paymentState, setPaymentState] = React.useState({
        isProcessing: false,
        paymentReference: null
    });

    const config = {
        reference: (new Date()).getTime().toString(),
        email: email,
        amount: amount * 100,
        publicKey: PAYSTACK_PUBLIC_KEY,
    };

    const handlePaystackSuccessAction = (reference) => {
        console.log('Payment successful with reference:', reference.reference);
        
        setPaymentState(prev => ({
            ...prev,
            isProcessing: true,
            reference: reference.reference
        }));

        if (paymentType === 'appointment' && compData) {
            dispatch(createPayment(
                user.id, 
                amount, 
                reference.reference, 
                user.location || user.address || 'Not specified', 
                notes,
                compData 
            ));
        } else {
            dispatch(createPayment(
                user.id, 
                amount, 
                reference.reference, 
                user.location || user.address || 'Not specified', 
                notes
            ));
        }
    };

    const handlePaystackCloseAction = () => {
        const errorMessage = 'Payment was cancelled or failed';
        
        toast.error(errorMessage, {
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });

        setPaymentState({
            isProcessing: false,
            paymentReference: null
        });

        if (onPaymentError) {
            onPaymentError(errorMessage);
        }
    };

    const componentProps = {
        ...config,
        text: buttonText,
        onSuccess: (reference) => handlePaystackSuccessAction(reference),
        onClose: handlePaystackCloseAction,
    };

    useEffect(() => {
        if (success && paymentState.isProcessing) {
            toast.success('Payment successful!', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            if (paymentType === 'appointment' && compData) {
                if (!appt.loading && !appt.success) {
                    dispatch(bookAppointmentAction(compData));
                }
            } else if (url) {
                // Reset payment state before navigation to prevent auto-navigation issues
                dispatch(resetPayment());
                
                navigate(`/${url}`, { 
                    state: { 
                        data: compData,
                        paymentReference: paymentState.paymentReference,
                        paymentData: data
                    } 
                });
            }

            setPaymentState({
                isProcessing: false,
                paymentReference: null
            });
        }

        if (error && paymentState.isProcessing) {
            toast.error(error || 'Payment processing failed', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            setPaymentState({
                isProcessing: false,
                paymentReference: null
            });

            if (onPaymentError) {
                onPaymentError(error);
            }
        }
    }, [success, error, paymentState.isProcessing]);

    // Handle appointment booking success
    useEffect(() => {
        if (appt.success && paymentType === 'appointment' && success) {
            toast.success('Appointment booked successfully!', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            if (url) {
                // Reset payment state before navigation to prevent auto-navigation issues
                dispatch(resetPayment());
                dispatch({ type: "BOOK_APPOINTMENT_RESET" });
                
                navigate(`/${url}`, { 
                    state: { 
                        data: compData,
                        paymentReference: paymentState.paymentReference,
                        appointmentData: appt.data
                    } 
                });
            }
        }

        if (appt.error && paymentType === 'appointment') {
            toast.error(appt.error || 'Failed to book appointment', {
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    }, [appt.success, appt.error]);

    return (
        <PaystackButton 
            className={buttonClassName}
            disabled={loading || appt.loading || paymentState.isProcessing}
            {...componentProps} 
        />
    );
}
